/**
 * Admin Database browser.
 *
 * Four-collection CRUD: candidates · attempts · licenses · concept_questions.
 *
 *  Layout:                                                          ┌────┐
 *   ┌────────────┬──────────────────────────────────────────────────┤    │
 *   │ collections│ search bar  +  pagination  +  count              │    │
 *   │   list      ─────────────────────────────────────────────────┐│    │
 *   │            │ rows table (id, candidate, key fields)          ││ JSON
 *   │            │ click row → load detail panel on the right       ││ panel
 *   └────────────┴──────────────────────────────────────────────────┘    │
 *                                                                    └────┘
 *
 *  Detail panel:
 *    • View pretty JSON
 *    • Edit (textarea raw JSON)  → PUT  → refresh
 *    • Delete  → DELETE  → refresh list
 */
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Database, RefreshCw, Search, Trash2, Save, X, AlertTriangle, FileText, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import DownloadButton from "@/components/DownloadButton";
import {
  adminDbIndex,
  adminDbList,
  adminDbGet,
  adminDbUpdate,
  adminDbDelete,
  certificateUrl,
  attemptCertificateUrl,
} from "@/lib/api";

const PAGE_SIZE = 25;

/** Curate which fields surface in the table view per collection.
 *  Falls back to `id` + first 3 non-id keys for anything we don't pin. */
const TABLE_COLS = {
  candidates: ["id", "name", "email", "country"],
  attempts: ["id", "candidate_name", "candidate_country", "overall_outcome", "license_id", "completed_at"],
  licenses: ["id", "candidate_name", "overall_outcome", "issued_at", "expires_at", "revoked"],
  concept_questions: ["id", "concept_id", "position", "source", "correct"],
};


export default function DatabaseBrowser({ collection = null, title = null } = {}) {
  const [index, setIndex] = useState([]);
  const [active, setActive] = useState(collection || "candidates");
  const locked = !!collection;
  const [data, setData] = useState({ docs: [], total: 0 });
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);  // selected document id
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const loadIndex = async () => {
    try { setIndex((await adminDbIndex()).collections || []); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await adminDbList(active, { q, limit: PAGE_SIZE, skip: page * PAGE_SIZE });
      setData(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadIndex(); }, []);
  // Re-sync `active` when the parent passes a new `collection` prop
  // (e.g. Admin tab switches from Candidates → Licenses). Without this,
  // the component reuses its first-mount `active` value forever.
  useEffect(() => {
    if (collection && collection !== active) {
      setActive(collection);
      setPage(0);
      setSelected(null);
      setDetail(null);
      setQ("");
      setData({ docs: [], total: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection]);
  useEffect(() => { setPage(0); setSelected(null); setDetail(null); }, [active]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active, page]);
  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    adminDbGet(active, selected).then(setDetail).catch((e) => toast.error(e.message));
  }, [active, selected]);

  const cols = useMemo(() => {
    if (TABLE_COLS[active]) return TABLE_COLS[active];
    const sample = data.docs?.[0] || {};
    return ["id", ...Object.keys(sample).filter((k) => k !== "id").slice(0, 3)];
  }, [active, data.docs]);

  const submitSearch = (e) => { e.preventDefault(); setPage(0); load(); };

  const onSave = async () => {
    try {
      const payload = JSON.parse(draft);
      await adminDbUpdate(active, selected, payload);
      toast.success("Saved");
      setEditing(false);
      await load();
      const fresh = await adminDbGet(active, selected);
      setDetail(fresh);
      await loadIndex();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Invalid JSON");
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete this ${active.slice(0, -1)} permanently? This cannot be undone.`)) return;
    try {
      await adminDbDelete(active, selected);
      toast.success("Deleted");
      setSelected(null); setDetail(null);
      await load(); await loadIndex();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div data-testid={`db-browser-${active}`} className="mt-8">
      <div className="flex items-center gap-2 text-sm text-[var(--iddl-muted)]">
        <Database size={14} />
        <span>
          {title ? `${title} — ` : ""}
          Direct database access — edits and deletes are immediate and not reversible.
        </span>
      </div>

      <div className={`grid mt-4 border border-[var(--iddl-rule)] ${locked ? "lg:grid-cols-[1fr_minmax(0,420px)]" : "lg:grid-cols-[200px_1fr_minmax(0,420px)]"}`}>
        {/* Collection tabs (left rail) — hidden when locked to a single collection */}
        {!locked && (
          <div className="border-r border-[var(--iddl-rule)] surface" data-testid="db-collections">
            {index.map((c) => (
              <button
                key={c.name}
                type="button"
                onClick={() => setActive(c.name)}
                data-testid={`db-collection-${c.name}`}
                className={`w-full text-left px-3 py-3 border-b border-[var(--iddl-rule)] hover:bg-[var(--iddl-paper)] ${active === c.name ? "bg-[var(--iddl-paper)] font-semibold" : ""}`}
              >
                <div className="font-display text-sm">{c.name}</div>
                <div className="label-mono">{c.count} docs</div>
              </button>
            ))}
          </div>
        )}

        {/* Table */}
        <div className="min-w-0">
          <form onSubmit={submitSearch} className="flex items-center gap-2 p-3 border-b border-[var(--iddl-rule)]">
            <Search size={14} />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by id / name / email / concept…"
              data-testid="db-search"
              className="h-8 rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)]"
            />
            <Button type="submit" variant="outline" size="sm" className="rounded-none" data-testid="btn-db-search">
              Search
            </Button>
            <Button type="button" variant="outline" size="sm" className="rounded-none" onClick={load} data-testid="btn-db-refresh">
              <RefreshCw size={12} />
            </Button>
          </form>

          {err && (
            <div className="p-3 text-xs text-rose-600 flex items-center gap-2">
              <AlertTriangle size={12} /> {err}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="db-table">
              <thead>
                <tr className="surface border-b border-[var(--iddl-rule)]">
                  {cols.map((c) => (
                    <th key={c} className="text-left p-2 label-mono">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={cols.length} className="p-4 label-mono text-[var(--iddl-muted)]">Loading…</td></tr>
                )}
                {!loading && data.docs.length === 0 && (
                  <tr><td colSpan={cols.length} className="p-4 label-mono text-[var(--iddl-muted)]">No documents.</td></tr>
                )}
                {!loading && data.docs.map((d, i) => (
                  <tr
                    key={d.id || i}
                    data-testid={`db-row-${d.id}`}
                    onClick={() => setSelected(d.id)}
                    className={`border-b border-[var(--iddl-rule)] cursor-pointer hover:bg-[var(--iddl-surface)] ${selected === d.id ? "bg-[var(--iddl-surface)]" : ""}`}
                  >
                    {cols.map((c) => (
                      <td key={c} className="p-2 font-mono-disp text-[12px] truncate max-w-[260px]">
                        {fmtCell(d[c])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-2 p-3 border-t border-[var(--iddl-rule)] text-xs">
            <span>{data.total} total</span>
            <span className="ms-auto label-mono">Page {page + 1} / {totalPages}</span>
            <Button type="button" variant="outline" size="sm" className="rounded-none" disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="btn-db-prev">
              <ChevronLeft size={12} />
            </Button>
            <Button type="button" variant="outline" size="sm" className="rounded-none" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="btn-db-next">
              <ChevronRight size={12} />
            </Button>
          </div>
        </div>

        {/* Detail panel */}
        <div className="border-l border-[var(--iddl-rule)] min-h-[400px] flex flex-col" data-testid="db-detail">
          {!selected && (
            <div className="p-6 label-mono text-[var(--iddl-muted)] grid place-items-center h-full">
              <div>
                <FileText size={20} className="mx-auto" />
                <div className="mt-2 text-center">Click a row to inspect.</div>
              </div>
            </div>
          )}
          {selected && !detail && (
            <div className="p-6 label-mono">Loading document…</div>
          )}
          {selected && detail && (
            <>
              <div className="p-3 border-b border-[var(--iddl-rule)] flex items-center gap-2">
                <code className="font-mono-disp text-xs flex-1 truncate">{selected}</code>
                {active === "licenses" && !editing && (
                  <DownloadButton
                    url={certificateUrl(selected)}
                    filename={`${selected}.pdf`}
                    label="Download licence PDF"
                    data-testid="btn-license-download-pdf"
                    icon={<Download size={12} className="ml-2" />}
                    className="bg-amber-600 hover:bg-amber-700 text-white border-amber-700"
                  />
                )}
                {active === "attempts" && !editing && detail?.current_step === "complete" && (
                  <DownloadButton
                    url={attemptCertificateUrl(selected)}
                    filename={`${selected}.pdf`}
                    label="Download attempt PDF"
                    data-testid="btn-attempt-download-pdf"
                    icon={<Download size={12} className="ml-2" />}
                  />
                )}
                {!editing ? (
                  <>
                    <Button type="button" size="sm" variant="outline" className="rounded-none" data-testid="btn-db-edit" onClick={() => {
                      setDraft(JSON.stringify(detail, null, 2));
                      setEditing(true);
                    }}>
                      Edit
                    </Button>
                    <Button type="button" size="sm" variant="outline" className="rounded-none text-rose-600 border-rose-300" data-testid="btn-db-delete" onClick={onDelete}>
                      <Trash2 size={12} />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button type="button" size="sm" className="rounded-none btn-ink" onClick={onSave} data-testid="btn-db-save">
                      <Save size={12} className="mr-1" /> Save
                    </Button>
                    <Button type="button" size="sm" variant="outline" className="rounded-none" onClick={() => setEditing(false)} data-testid="btn-db-cancel">
                      <X size={12} />
                    </Button>
                  </>
                )}
              </div>
              <div className="flex-1 overflow-auto p-3">
                {editing ? (
                  <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={28}
                    className="font-mono-disp text-[11px] rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)]"
                    data-testid="db-editor"
                  />
                ) : (
                  <pre className="font-mono-disp text-[11px] whitespace-pre-wrap break-words" data-testid="db-json">
                    {JSON.stringify(detail, null, 2)}
                  </pre>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function fmtCell(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "object") return JSON.stringify(v).slice(0, 80);
  return String(v);
}
